import {Radio} from "antd"
import moment from "moment"
import {useState} from "react"
import {useTranslation} from "react-i18next"
import {BaseButton} from "components"
import {toastSuccess} from "helpers"
import {usePaymentProviderConfigs} from "hooks/usePaymentProviderConfigs"
import {DirectEmbedPayment} from "sections/StudentAccount/Payment/PaymentModal/parts/DirectEmbedPayment"
import {PaymentMethod as DirectEmbedTokenizeMethod} from "sections/StudentAccount/Payment/PaymentModal/parts/DirectEmbedPayment/DirectEmbedPayment"
import {
    CreateRecurringPaymentPayload,
    PaymentProvider,
    RecurringPaymentInterval,
    StudentPaymentSupportedCurrency
} from "types/student-account/student-payment"
import styles from "./RecurringPayments.module.css"

type Props = {
    profileId: number
    amountInCents: number
    currency: StudentPaymentSupportedCurrency
    interval: RecurringPaymentInterval
    intervalCount?: number
    cancelAt: moment.Moment | null
    paymentPlanItems?: CreateRecurringPaymentPayload["paymentPlanItems"]
    paymentPlanId?: number | null
    onSuccess: () => void
}

// DirectEmbed has no inline-fields tokenization (like Accept.js) — card/bank details are only
// collected via Aliaswire's hosted modal. Rather than duplicate that script-loading/tokenize
// dance, this reuses the same DirectEmbedPayment component the checkout flow uses, just switched
// into its recurring-setup mode.
//
// Unlike the other providers' forms, this doesn't offer a saved-payment-method choice: DirectEmbed
// payment methods don't carry last4/cardBrand/expiry (Aliaswire's tokenize response doesn't
// reliably expose them), so a saved-method list here would just show unlabeled placeholders. Every
// subscription is set up with a freshly tokenized card/bank account instead.
export const DirectEmbedRecurringSubscriptionForm = ({
    profileId,
    amountInCents,
    currency,
    interval,
    intervalCount,
    cancelAt,
    paymentPlanItems,
    paymentPlanId,
    onSuccess
}: Props) => {
    const {t} = useTranslation(["payment", "common"])
    const {paymentProviderConfigs} = usePaymentProviderConfigs(profileId)
    const providerConfig = paymentProviderConfigs.find((c) => c.provider === PaymentProvider.DirectEmbed) as any

    const [tokenizeMethod, setTokenizeMethod] = useState<DirectEmbedTokenizeMethod>(DirectEmbedTokenizeMethod.CARD)
    const [openTokenizeModal, setOpenTokenizeModal] = useState(false)

    if (amountInCents <= 0) {
        return null
    }

    return (
        <div>
            <Radio.Group
                className="mb-8 mt-8"
                value={tokenizeMethod}
                onChange={(e) => setTokenizeMethod(e.target.value)}>
                <Radio value={DirectEmbedTokenizeMethod.CARD}>Card</Radio>
                <Radio value={DirectEmbedTokenizeMethod.ACH}>Bank Account (ACH)</Radio>
            </Radio.Group>
            <BaseButton
                size="small"
                title={t("common:action.add")}
                className={`${styles.payButton} w-full mt-24`}
                onClick={() => setOpenTokenizeModal(true)}
            />
            {providerConfig && (
                <DirectEmbedPayment
                    isOpen={openTokenizeModal}
                    setIsOpen={setOpenTokenizeModal}
                    method={tokenizeMethod}
                    publishedKey={providerConfig.publishedKey}
                    providerConfig={providerConfig}
                    studentId={profileId}
                    paymentAmount={amountInCents / 100}
                    currency={currency}
                    customNote=""
                    isPaymentRecurring
                    recurringInterval={interval}
                    recurringIntervalCount={intervalCount}
                    recurringCancelAt={cancelAt?.toISOString() ?? null}
                    recurringPaymentPlanItems={paymentPlanItems}
                    recurringPaymentPlanId={paymentPlanId}
                    onRecurringSuccess={() => {
                        toastSuccess(t("msg.recurringPayment.addedSuccessfully"))
                        onSuccess()
                    }}
                />
            )}
        </div>
    )
}
